import React, { useState } from "react";
import axios from "axios";
import { useLang } from "../context/LanguageContext";
import useReveal from "../hooks/useReveal";
import { contactInfo } from "../mock";
import { Mail, MessageCircle, Phone, Send, CheckCircle2, AlertCircle, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;
  const ref = useReveal();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [status, setStatus] = useState(null); // null | 'sending' | 'success' | 'error' | 'invalid'

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setStatus("invalid");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  const contacts = [
    { icon: Mail, label: c.contactLabels.email, value: contactInfo.email, href: `mailto:${contactInfo.email}` },
    { icon: MessageCircle, label: c.contactLabels.whatsapp, value: contactInfo.phone, href: contactInfo.whatsapp },
    { icon: Phone, label: c.contactLabels.phone, value: contactInfo.phone, href: `tel:+${contactInfo.phoneRaw}` },
  ];

  return (
    <section id="contacto" className="bg-[#081527] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div ref={ref} className="reveal grid lg:grid-cols-2 gap-12">
          {/* Left info */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="section-line" />
              <span className="text-xs font-700 uppercase tracking-[0.25em] font-display text-[#f5871f]">{c.eyebrow}</span>
            </div>
            <h2 className="font-display font-700 text-4xl md:text-5xl uppercase text-white leading-tight">{c.title}</h2>
            <h3 className="mt-4 text-xl text-[#f5871f] font-display font-600">{c.subtitle}</h3>
            <p className="mt-4 text-white/60 leading-relaxed max-w-lg">{c.text}</p>

            <div className="mt-10 space-y-4">
              {contacts.map((item, i) => {
                const Icon = item.icon;
                return (
                  <a
                    key={i}
                    href={item.href}
                    target={item.label === c.contactLabels.whatsapp ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 group"
                  >
                    <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/5 border border-white/10 group-hover:border-[#f5871f]/50 transition-colors">
                      <Icon className="w-5 h-5 text-[#f5871f]" />
                    </span>
                    <div>
                      <div className="text-white/50 text-xs uppercase tracking-widest font-display">{item.label}</div>
                      <div className="text-white font-600 group-hover:text-[#f5871f] transition-colors">{item.value}</div>
                    </div>
                  </a>
                );
              })}
              <div className="flex items-center gap-4">
                <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/5 border border-white/10">
                  <MapPin className="w-5 h-5 text-[#f5871f]" />
                </span>
                <div className="text-white font-600">{contactInfo.location}</div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <h3 className="font-display font-700 text-2xl uppercase text-[#0d2140]">{c.formTitle}</h3>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-600 uppercase tracking-wide text-[#0d2140]/60">{c.fields.name}</label>
                  <input name="name" value={form.name} onChange={onChange} placeholder={c.placeholders.name}
                    className="mt-1.5 w-full rounded-xl border border-[#0d2140]/15 px-4 py-3 text-[#0d2140] focus:outline-none focus:border-[#f5871f] focus:ring-2 focus:ring-[#f5871f]/20 transition" />
                </div>
                <div>
                  <label className="text-xs font-600 uppercase tracking-wide text-[#0d2140]/60">{c.fields.phone}</label>
                  <input name="phone" value={form.phone} onChange={onChange} placeholder={c.placeholders.phone}
                    className="mt-1.5 w-full rounded-xl border border-[#0d2140]/15 px-4 py-3 text-[#0d2140] focus:outline-none focus:border-[#f5871f] focus:ring-2 focus:ring-[#f5871f]/20 transition" />
                </div>
              </div>
              <div>
                <label className="text-xs font-600 uppercase tracking-wide text-[#0d2140]/60">{c.fields.email}</label>
                <input name="email" type="email" value={form.email} onChange={onChange} placeholder={c.placeholders.email}
                  className="mt-1.5 w-full rounded-xl border border-[#0d2140]/15 px-4 py-3 text-[#0d2140] focus:outline-none focus:border-[#f5871f] focus:ring-2 focus:ring-[#f5871f]/20 transition" />
              </div>
              <div>
                <label className="text-xs font-600 uppercase tracking-wide text-[#0d2140]/60">{c.fields.message}</label>
                <textarea name="message" value={form.message} onChange={onChange} rows={4} placeholder={c.placeholders.message}
                  className="mt-1.5 w-full rounded-xl border border-[#0d2140]/15 px-4 py-3 text-[#0d2140] focus:outline-none focus:border-[#f5871f] focus:ring-2 focus:ring-[#f5871f]/20 transition resize-none" />
              </div>

              {status === "invalid" && (
                <p className="flex items-center gap-2 text-red-600 text-sm"><AlertCircle className="w-4 h-4" />{c.validation}</p>
              )}
              {status === "success" && (
                <p className="flex items-center gap-2 text-green-600 text-sm"><CheckCircle2 className="w-4 h-4" />{c.success}</p>
              )}
              {status === "error" && (
                <p className="flex items-center gap-2 text-red-600 text-sm"><AlertCircle className="w-4 h-4" />{c.error}</p>
              )}

              <button type="submit" disabled={status === "sending"}
                className="btn-orange w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-base font-700 uppercase tracking-wide font-display disabled:opacity-60">
                {status === "sending" ? c.sending : c.submit}
                {status !== "sending" && <Send className="w-4 h-4" />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
